/* ==========================================================================
   Harper Reborn — product-page blocks (main-product.liquid)
   HP.money(cents, format)      money formatter driven by the shop's money format
   <hp-countdown>               → see hp-countdown.js (data-hp-countdown* contract)
   <hp-urgency>                 stock / urgency bar ([stok] placeholder)
   <hp-shipping-estimate>       order-by / ships / delivered dates
   <hp-discount-code>           click-to-copy code (uses harper-motion's data-hp-copy)
   <hp-together>                "birlikte al" checkbox bundle → /cart/add.js
   [data-hp-btn-price]          price inside the add-to-cart button
   Discount codes themselves go through HP.cart (assets/hp-cart-extras.js).
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var HP = (window.HP = window.HP || {});

  /* ---------------- money ---------------- */
  function group(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }
  HP.money = function (cents, format) {
    cents = Math.round(Number(cents) || 0);
    format = format || HP.moneyFormat || '{{amount}}';
    var val = Math.abs(cents) / 100;
    var sign = cents < 0 ? '-' : '';
    var out;
    if (/amount_no_decimals_with_comma_separator/.test(format)) {
      out = group(String(Math.round(val)), '.');
    } else if (/amount_with_comma_separator/.test(format)) {
      var s = val.toFixed(2).split('.');
      out = group(s[0], '.') + ',' + s[1];
    } else if (/amount_no_decimals/.test(format)) {
      out = group(String(Math.round(val)), ',');
    } else if (/amount_with_apostrophe_separator/.test(format)) {
      var a = val.toFixed(2).split('.');
      out = group(a[0], "'") + '.' + a[1];
    } else {
      var d = val.toFixed(2).split('.');
      out = group(d[0], ',') + '.' + d[1];
    }
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, sign + out).trim();
  };

  function subscribeSafe(name, fn) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[name]) {
      return subscribe(PUB_SUB_EVENTS[name], fn);
    }
    return null;
  }

  /* ---------------- <hp-urgency> ---------------- */
  class HpUrgency extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-urg-text]');
      this.fill = this.querySelector('[data-urg-fill]');
      this.template = this.dataset.template || '';
      this.full = Math.max(1, parseFloat(this.dataset.full || '50'));
      this.render(this.dataset.mode === 'manual' ? parseFloat(this.dataset.manual || '0') : this.currentStock());
      if (this.dataset.mode !== 'manual') {
        this.unsub = subscribeSafe('variantChange', (e) => {
          var v = e && e.data ? e.data.variant : null;
          this.render(v ? this.stockOf(v) : null);
        });
      }
    }
    disconnectedCallback() {
      if (this.unsub) this.unsub();
    }
    currentStock() {
      return this.dataset.tracked === 'true' ? parseFloat(this.dataset.stock || '0') : null;
    }
    stockOf(v) {
      return v.inventory_management === 'shopify' ? parseFloat(v.inventory_quantity) : null;
    }
    render(stock) {
      // In the theme editor keep the block visible (with the manual number) so it can be clicked.
      if ((stock === null || isNaN(stock) || stock <= 0) && window.Shopify && window.Shopify.designMode) {
        stock = parseFloat(this.dataset.manual || '7');
      }
      if (stock === null || isNaN(stock) || stock <= 0) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      var n = Math.round(stock);
      if (this.textEl) {
        this.textEl.innerHTML = this.template.replace(/\[stok\]/g, '<b>' + n + '</b>');
      }
      if (this.fill) {
        var pct = Math.max(4, Math.min(100, (n / this.full) * 100));
        this.style.setProperty('--hp-urg', pct.toFixed(1) + '%');
      }
    }
  }
  if (!customElements.get('hp-urgency')) customElements.define('hp-urgency', HpUrgency);

  /* ---------------- <hp-shipping-estimate> ---------------- */
  function addDays(d, n, skipWeekends) {
    var out = new Date(d.getTime());
    var left = Math.max(0, Math.round(n));
    while (left > 0) {
      out.setDate(out.getDate() + 1);
      if (skipWeekends && (out.getDay() === 0 || out.getDay() === 6)) continue;
      left--;
    }
    if (skipWeekends) {
      while (out.getDay() === 0 || out.getDay() === 6) out.setDate(out.getDate() + 1);
    }
    return out;
  }
  function fmtDate(d, withWeekday) {
    try {
      return d.toLocaleDateString('en-GB', withWeekday ? { weekday: 'short', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long' });
    } catch (e) {
      return d.getDate() + '.' + (d.getMonth() + 1);
    }
  }
  class HpShippingEstimate extends HTMLElement {
    connectedCallback() {
      var skip = this.dataset.skipWeekends === 'true';
      var cutoff = (this.dataset.cutoff || '').trim();
      var now = new Date();
      var base = new Date(now.getTime());
      var beforeCutoff = false;
      if (/^\d{1,2}:\d{2}$/.test(cutoff)) {
        var hm = cutoff.split(':');
        var c = new Date(now.getTime());
        c.setHours(parseInt(hm[0], 10), parseInt(hm[1], 10), 0, 0);
        beforeCutoff = now < c;
        if (!beforeCutoff) base.setDate(base.getDate() + 1);
      }
      if (skip) {
        while (base.getDay() === 0 || base.getDay() === 6) base.setDate(base.getDate() + 1);
      }
      var ship = addDays(base, parseFloat(this.dataset.ship || '1'), skip);
      var dMin = addDays(ship, parseFloat(this.dataset.min || '2'), skip);
      var dMax = addDays(ship, parseFloat(this.dataset.max || '4'), skip);
      var deliver = dMin.getTime() === dMax.getTime() ? fmtDate(dMin) : fmtDate(dMin) + ' – ' + fmtDate(dMax);

      var el;
      if ((el = this.querySelector('[data-ship-cutoff]'))) {
        el.hidden = !(beforeCutoff && cutoff);
        el.textContent = 'Order before ' + cutoff + ' today';
      }
      if ((el = this.querySelector('[data-ship-order]'))) el.textContent = fmtDate(now, true);
      if ((el = this.querySelector('[data-ship-ship]'))) el.textContent = fmtDate(ship, true);
      if ((el = this.querySelector('[data-ship-deliver]'))) el.textContent = deliver;
      this.hidden = false;
    }
  }
  if (!customElements.get('hp-shipping-estimate')) customElements.define('hp-shipping-estimate', HpShippingEstimate);

  /* ---------------- <hp-discount-code> ----------------
     The copy itself is done by harper-motion's [data-hp-copy] hook; here we
     only queue/apply the code on the cart. */
  class HpDiscountCode extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-hp-copy]');
      if (!this.btn) return;
      this.onClick = () => {
        var code = this.btn.dataset.hpCopy || '';
        if (!code || !HP.cart) return;
        HP.cart.queue(code);
        HP.cart.fetch().then((c) => {
          if (c && c.item_count > 0) return HP.cart.add(code);
        }).catch(function () {});
      };
      this.btn.addEventListener('click', this.onClick);
    }
    disconnectedCallback() {
      if (this.btn && this.onClick) this.btn.removeEventListener('click', this.onClick);
    }
  }
  if (!customElements.get('hp-discount-code')) customElements.define('hp-discount-code', HpDiscountCode);

  /* ---------------- <hp-together> ---------------- */
  class HpTogether extends HTMLElement {
    connectedCallback() {
      this.fmt = this.dataset.moneyFormat || HP.moneyFormat;
      this.section = this.dataset.section;
      this.totalEl = this.querySelector('[data-together-total]');
      this.btn = this.querySelector('[data-together-submit]');
      this.msg = this.querySelector('[data-together-msg]');
      this.current = this.querySelector('[data-together-current]');
      this.boxes = [].slice.call(this.querySelectorAll('[data-together-item]'));
      this.onChange = () => this.update();
      this.boxes.forEach((b) => b.addEventListener('change', this.onChange));
      if (this.btn) {
        this.onSubmit = (e) => {
          e.preventDefault();
          this.add();
        };
        this.btn.addEventListener('click', this.onSubmit);
      }
      if (this.current) {
        this.unsub = subscribeSafe('variantChange', (e) => {
          var v = e && e.data ? e.data.variant : null;
          if (v) {
            this.current.dataset.id = v.id;
            this.current.dataset.price = v.price;
            this.current.disabled = !v.available;
            var p = this.querySelector('[data-together-current-price]');
            if (p) p.textContent = HP.money(v.price, this.fmt);
          } else {
            this.current.disabled = true;
          }
          this.update();
        });
      }
      this.update();
    }
    disconnectedCallback() {
      this.boxes.forEach((b) => b.removeEventListener('change', this.onChange));
      if (this.btn && this.onSubmit) this.btn.removeEventListener('click', this.onSubmit);
      if (this.unsub) this.unsub();
    }
    selected() {
      return this.boxes.filter((b) => b.checked && !b.disabled);
    }
    update() {
      var total = 0;
      this.boxes.forEach((b) => {
        var row = b.closest('[data-together-row]');
        if (row) {
          row.classList.toggle('is-off', !b.checked);
          row.classList.toggle('is-disabled', b.disabled);
        }
        if (b.checked && !b.disabled) total += (parseFloat(b.dataset.price) || 0) * (parseInt(b.dataset.qty || '1', 10) || 1);
      });
      if (this.totalEl) this.totalEl.textContent = HP.money(total, this.fmt);
      if (this.btn) this.btn.disabled = this.selected().length === 0;
    }
    say(text, ok) {
      if (!this.msg) return;
      this.msg.textContent = text || '';
      this.msg.hidden = !text;
      this.msg.classList.toggle('is-ok', !!ok);
    }
    add() {
      var items = this.selected().map((b) => ({ id: parseInt(b.dataset.id, 10), quantity: parseInt(b.dataset.qty || '1', 10) || 1 }));
      if (!items.length) return;
      var cart = doc.querySelector('cart-drawer') || doc.querySelector('cart-notification');
      var body = { items: items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      var cfg = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
      cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
      cfg.body = JSON.stringify(body);
      this.classList.add('is-busy');
      this.say('');
      fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', cfg)
        .then((r) => r.json())
        .then((res) => {
          if (res.status) {
            this.say(res.description || res.message || (window.cartStrings && window.cartStrings.error) || 'Could not add to basket.');
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hp-together', cartData: res });
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(res);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
          this.say('Added to basket ✓', true);
        })
        .catch(() => this.say((window.cartStrings && window.cartStrings.error) || 'Something went wrong.'))
        .finally(() => this.classList.remove('is-busy'));
    }
  }
  if (!customElements.get('hp-together')) customElements.define('hp-together', HpTogether);

  /* ---------------- price inside the ATC button ---------------- */
  function initBtnPrice() {
    doc.querySelectorAll('[data-hp-btn-price]').forEach(function (span) {
      if (span.dataset.hpBound) return;
      span.dataset.hpBound = '1';
      var fmt = span.dataset.moneyFormat || HP.moneyFormat;
      var unit = parseFloat(span.dataset.price || '0');
      var tierText = '';
      function paint() {
        span.textContent = tierText || HP.money(unit, fmt);
      }
      subscribeSafe('variantChange', function (e) {
        var v = e && e.data ? e.data.variant : null;
        if (v) unit = parseFloat(v.price) || 0;
        span.hidden = !v || v.available === false;
        requestAnimationFrame(paint);
      });
      doc.addEventListener('hp:qb:change', function (e) {
        var d = e.detail || {};
        tierText = d.totalText || '';
        paint();
      });
      paint();
    });
  }
  initBtnPrice();
  doc.addEventListener('shopify:section:load', initBtnPrice);
})();
